import React from 'react';
import {
  formatCountdown,
  formatGateResourceTz,
  formatGateLocalTz,
  resourceTzLabel,
  localTzLabel,
  groupBySession,
  CARD_BASE,
} from './scheduleFormat';
import { formatTargetDay } from '../../../utils/datetime';

const ONE_HOUR_MS = 60 * 60 * 1000;

export const HorizonChip = ({ label, preview }) => (
  <span
    className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold ${
      preview
        ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:ring-amber-800/60'
        : 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
    }`}
  >
    {label}
  </span>
);

const sessionSubtitle = (items) => {
  const parts = [];
  const hasIntraday = items.some(
    (i) => i.challenge_type === 'intraday' || i.cadence === 'hourly'
  );
  if (hasIntraday) parts.push('rolling · then hourly');

  const dayAheadDays = items
    .filter((i) => i.challenge_type !== 'intraday' && i.target_day)
    .map((i) => i.target_day)
    .sort();
  if (dayAheadDays.length === 1) {
    parts.push(`target ${formatTargetDay(dayAheadDays[0])}`);
  } else if (dayAheadDays.length > 1) {
    parts.push(
      `targets ${formatTargetDay(dayAheadDays[0])}, ${formatTargetDay(
        dayAheadDays[dayAheadDays.length - 1]
      )}`
    );
  }
  return parts.join(' · ');
};

export default function NextOpeningsPanel({ openings = [], loading = false, now = Date.now() }) {
  const sessions = groupBySession(openings);

  return (
    <section>
      <div className="mb-3">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
          Next openings
        </h2>
        <p className="mt-1 text-[11px] leading-4 text-gray-400 dark:text-gray-500">
          Each market session and the horizons opening at it. Submit before the
          gate closure (the deadline).
        </p>
      </div>
      <div className={CARD_BASE}>
        {loading && sessions.length === 0 ? (
          <div className="p-4 text-sm text-gray-500 dark:text-gray-400">Loading…</div>
        ) : sessions.length === 0 ? (
          <div className="p-4 text-sm text-gray-500 dark:text-gray-400">
            No openings in this window.
          </div>
        ) : (
          <ul
            className="divide-y divide-gray-100 dark:divide-gray-700/60"
            data-testid="sessions-list"
          >
            {sessions.map((session) => {
              const gateMs = new Date(session.gateClosure).getTime();
              const imminent = session.gateClosure && gateMs - now <= ONE_HOUR_MS;
              const openMs = session.sessionOpen
                ? new Date(session.sessionOpen).getTime()
                : null;
              const openVerb = openMs !== null && openMs <= now ? 'Opened' : 'Opens';
              const subtitle = sessionSubtitle(session.items);
              return (
                <li key={session.gateClosure} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2.5">
                      <span
                        className={`h-2 w-2 shrink-0 rounded-full ${
                          imminent ? 'bg-brand-500' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                        aria-hidden="true"
                      />
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                          Gate closes {formatGateResourceTz(session.gateClosure)}{' '}
                          {resourceTzLabel(session.gateClosure)}
                        </div>
                        <div className="text-[11px] text-gray-400 dark:text-gray-500">
                          {session.sessionOpen
                            ? `${openVerb} ${formatGateResourceTz(session.sessionOpen)}`
                            : ''}
                          {session.sessionOpen && subtitle ? ' · ' : ''}
                          {subtitle}
                        </div>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-xs font-medium text-gray-800 dark:text-gray-200">
                        closes {formatCountdown(session.gateClosure, now)}
                      </div>
                      <div className="text-[11px] text-gray-400 dark:text-gray-500">
                        {formatGateLocalTz(session.gateClosure)}{' '}
                        {localTzLabel(session.gateClosure)}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5 pl-[1.125rem]">
                    {session.items.map((item) => (
                      <HorizonChip
                        key={item.horizon_label}
                        label={item.horizon_label}
                        preview={!item.is_paid}
                      />
                    ))}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
